import {
  type MutableRefObject,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from 'react'

import { getAbsoluteOffset } from '@lib/dom'
import {
  type CombinedListingStep,
  type CombinedListingToggleStepHandler,
  getCombinedListingProductDropdownOptions,
  ProductCombinedListingContext,
} from '@lib/product/combined-listing'
import { getOptionTitle } from '@lib/product/option'
import { ProductQuickAddContext } from '@lib/product/quick-add-context'
import { SiteContext } from '@lib/site-context'
import { StringsContext } from '@lib/strings-context'

import Accordion from '@components/accordion'
import OptionListItem from './combined-listing-option-list-item'

interface CombinedListingProductGlassSelectProps {
  isDefaultSelectionLoadedRef: MutableRefObject<boolean>
  glassProductId?: number
  openStep?: CombinedListingStep
  isScreenSmallOrLarger: boolean
  onToggleStep: CombinedListingToggleStepHandler
}

const CombinedListingProductGlassSelect = ({
  isDefaultSelectionLoadedRef,
  glassProductId,
  openStep,
  isScreenSmallOrLarger,
  onToggleStep,
}: CombinedListingProductGlassSelectProps) => {
  const {
    combinedListingConfiguration,
    combinedListingActiveSize,
    addCombinedListingProduct,
    clearCombinedListingProductType,
  } = useContext(ProductCombinedListingContext)
  const { productQuickAdd } = useContext(ProductQuickAddContext)
  const { headerHeight } = useContext(SiteContext)
  const strings = useContext(StringsContext)

  const ref = useRef<HTMLDivElement | null>(null)

  const glassOptions = useMemo(
    () =>
      getCombinedListingProductDropdownOptions(
        combinedListingConfiguration?.glass ?? [],
        combinedListingActiveSize ?? null,
        strings.combinedListingNoGlass,
        strings.combinedListingNoGlassDescription,
      ),
    [combinedListingActiveSize, combinedListingConfiguration, strings],
  )

  const hasGlassOptions = useMemo(
    () => glassOptions.some((option) => !!option.value),
    [glassOptions],
  )

  const handleGlassChange = useCallback(
    (newProductId: string) => {
      isDefaultSelectionLoadedRef.current = true

      if (!newProductId) {
        clearCombinedListingProductType('glass')
        return
      }

      addCombinedListingProduct(Number(newProductId))
    },
    [
      addCombinedListingProduct,
      clearCombinedListingProductType,
      isDefaultSelectionLoadedRef,
    ],
  )

  const handleToggle = useCallback(
    (isOpen: boolean) => {
      onToggleStep('glass', isOpen)

      // Scrolling animation on mobile when opening the accordion
      if (isOpen && !isScreenSmallOrLarger && !productQuickAdd.isOpen) {
        setTimeout(() => {
          if (!ref.current) {
            return
          }

          const offsetTop = getAbsoluteOffset(ref.current).top
          window.scroll({
            top: Math.max(0, offsetTop - headerHeight),
            behavior: 'smooth',
          })
        }, 500)
      }
    },
    [headerHeight, isScreenSmallOrLarger, onToggleStep, productQuickAdd.isOpen],
  )

  if (!hasGlassOptions) {
    return null
  }

  return (
    <Accordion
      ref={ref}
      id="combined-listing-product-glass"
      title={
        <>
          {strings.combinedListingGlass}:{' '}
          <span className="!font-normal">
            {getOptionTitle(glassOptions, `${glassProductId ?? ''}`)}
          </span>
        </>
      }
      bordered
      open={openStep === 'glass'}
      onToggle={handleToggle}
    >
      <div className="flex flex-col gap-2.5">
        {glassOptions.map((option) => (
          <OptionListItem
            key={option.value}
            title={option.title}
            description={option.description}
            price={option.price}
            alt={option.title}
            photo={option.imageUrl}
            active={`${glassProductId ?? ''}` === option.value}
            onClick={() => handleGlassChange(option.value)}
          />
        ))}
      </div>
    </Accordion>
  )
}

export default CombinedListingProductGlassSelect
